import { CSSResultGroup, html, LitElement, nothing } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { consume } from '@lit/context'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { createEffect } from '@cloudsuite/mosaic-shared/scripts/signals.js'
import { PDP_PRODUCT_STORE, type PdpProductStore } from 'components/discovery/pdp/pdp-product/pdp-product-store.js'
import { productTileProductContext } from 'components/discovery/product/tile-parts/tile-product-context.js'
import { InternalProduct, ProductModel } from 'components/discovery/product/tile-parts/tile-parts-typing.js'

export const retailPriceVariants = [
  'body', 'body-s',
  'caption-l', 'caption-m', 'caption-s',
] as const

const emptyTileProduct: InternalProduct = new ProductModel().parseToInternal()

@customElement('csc-retail-price')
export class CSCRetailPrice extends LitElement {
  static override styles: CSSResultGroup = [hostStyle]

  @inject(PDP_PRODUCT_STORE)
  private store!: PdpProductStore

  @property({ type: String })
  locale: string = 'en-US'

  @property({ type: String, attribute: 'font-variant' })
  fontVariant: string = 'body-s'

  /** @deprecated Use `font-variant` instead. */
  @property({ type: String })
  set variant(value: string) {
    console.warn('<csc-retail-price> "variant" is deprecated, use "font-variant" instead.')
    this.fontVariant = value
  }

  get variant() {
    return this.fontVariant
  }

  @state()
  private consumerPrice: string | null = null

  @state()
  private currencyCode: string | null = null

  @consume({ context: productTileProductContext, subscribe: true })
  @state()
  private tileProduct: InternalProduct = emptyTileProduct

  /** True when an ancestor csc-base-product-tile provides real product data, i.e. when used as a PLP tile widget */
  private get isInTile(): boolean {
    return this.tileProduct !== emptyTileProduct
  }

  constructor() {
    super()
    createEffect(() => {
      const product = this.store.product()
      if (product) {
        this.consumerPrice = product.prices.consumer
        this.currencyCode = product.prices.currency_code
      }
    })
  }

  override render() {
    const consumerPrice = this.isInTile ? this.tileProduct.prices.consumer : this.consumerPrice
    const currencyCode = this.isInTile ? this.tileProduct.prices.currencyCode : this.currencyCode
    if (!consumerPrice || !currencyCode) return nothing

    const priceFormatted = Intl.NumberFormat(this.locale, {
      style: 'currency',
      currency: currencyCode
    }).format(Number(consumerPrice))

    return html`
      <cse-text as="p" variant="${this.fontVariant}">
        ${t('text_retail_price', { ns: 'csc-retail-price', default: 'Suggested retail price' })}
        ${priceFormatted}
      </cse-text>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-retail-price': CSCRetailPrice
  }
}
